import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import { ArrowRight, ArrowLeft, Plane, Check, Sparkles } from "lucide-react";

// Iter 27 — Return today's date in WIB (UTC+7) as YYYY-MM-DD for date-input min validation.
const todayWIB = () => {
  const now = new Date();
  // Add WIB offset (+7h) to UTC then format YYYY-MM-DD
  const wib = new Date(now.getTime() + (7 * 60 + now.getTimezoneOffset()) * 60_000);
  const yyyy = wib.getFullYear();
  const mm = String(wib.getMonth() + 1).padStart(2, "0");
  const dd = String(wib.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function CreateTrip() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [countries, setCountries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [suggested, setSuggested] = useState([]); // Smart Catalog: items from previous trips to same country
  const [loading, setLoading] = useState(false);
  const [filterByCountry, setFilterByCountry] = useState(true);  // Iter 20: default ON

  const [form, setForm] = useState({
    destination_country: "",
    destination_city: "",
    departure_date: "",
    return_date: "",
    notes: "",
    service_fee_percent: 0,  // deprecated — fees now per-item on each catalog entry
    catalog_item_ids: [],
    origin_location: "",
  });

  const [newItem, setNewItem] = useState({ name: "", price: "", description: "", photo_path: null, fee_type: "fixed", fee_value: "" });

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data));
    api.get("/catalog").then((r) => setCatalog(r.data));
    api.get("/locations").then((r) => setLocations(r.data));
  }, []);

  // Smart Catalog: when destination country changes, fetch & pre-select previous items to same country
  useEffect(() => {
    if (!form.destination_country) {
      setSuggested([]);
      return;
    }
    api.get(`/trips/catalog-suggest/${encodeURIComponent(form.destination_country)}`)
      .then((r) => {
        setSuggested(r.data);
        // Auto-pre-select suggestions ONLY if user hasn't manually picked anything yet
        setForm((f) => {
          if (f.catalog_item_ids.length > 0) return f;
          return { ...f, catalog_item_ids: r.data.map((i) => i.id) };
        });
      })
      .catch(() => setSuggested([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.destination_country]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const toggleCatalog = (id) => {
    if (form.catalog_item_ids.includes(id)) {
      set("catalog_item_ids", form.catalog_item_ids.filter((x) => x !== id));
    } else {
      set("catalog_item_ids", [...form.catalog_item_ids, id]);
    }
  };

  const addCatalogItem = async () => {
    if (!newItem.name || !newItem.price) {
      toast.error("Mohon isi nama item dan harga");
      return;
    }
    try {
      const r = await api.post("/catalog", {
        name: newItem.name,
        price: parseFloat(newItem.price),
        description: newItem.description,
        photo_path: newItem.photo_path,
        country: form.destination_country,
        fee_type: newItem.fee_type || "fixed",
        fee_value: parseFloat(newItem.fee_value || 0) || 0,
      });
      setCatalog([...catalog, r.data]);
      set("catalog_item_ids", [...form.catalog_item_ids, r.data.id]);
      setNewItem({ name: "", price: "", description: "", photo_path: null, fee_type: "fixed", fee_value: "" });
      toast.success("Item ditambahkan ke katalog");
    } catch (err) {
      toast.error(formatApiError(err) || "Gagal menambahkan item");
    }
  };

  const submit = async () => {
    if (!form.destination_country || !form.departure_date || !form.return_date) {
      toast.error("Mohon lengkapi detail trip");
      setStep(1);
      return;
    }
    if (!form.origin_location) {
      toast.error("Mohon pilih lokasi asal keberangkatan");
      setStep(1);
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/trips", form);
      toast.success("Trip terposting!");
      navigate(`/trips/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Gagal membuat trip");
    } finally {
      setLoading(false);
    }
  };

  // Flatten locations for the origin dropdown
  const originOptions = locations.flatMap((p) =>
    p.cities.map((c) => ({ value: c, label: `${c}, ${p.province}` }))
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="create-trip-page">
      <div>
        <div className="label-caps mb-2">Posting Trip</div>
        <h1 className="font-display text-4xl tracking-tight">Trip Jastip Baru</h1>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-0 border-y" style={{borderColor: "var(--app-border)"}}>
        {[1, 2, 3].map((n) => (
          <div key={n} className="flex-1 p-4 border-r last:border-r-0" style={{
            borderColor: "var(--app-border)",
            backgroundColor: step === n ? "var(--app-brand)" : "var(--app-surface)",
            color: step === n ? "white" : "var(--app-fg)",
          }}>
            <div className="label-caps opacity-70" style={{color: "inherit"}}>Step {n}</div>
            <div className="font-medium text-sm mt-1">
              {n === 1 ? "Detail Trip" : n === 2 ? "Item Katalog" : "Review"}
            </div>
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="surface p-8 space-y-5" data-testid="step-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="label-caps mb-2 block">Negara Tujuan *</label>
              <select className="input-flat" value={form.destination_country} onChange={(e) => set("destination_country", e.target.value)} data-testid="trip-country-input">
                <option value="">Pilih negara</option>
                {countries.map((c) => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps mb-2 block">Kota Tujuan</label>
              <input className="input-flat" value={form.destination_city} onChange={(e) => set("destination_city", e.target.value)} placeholder="cth. Tokyo" data-testid="trip-city-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-caps mb-2 block">Lokasi Asal Anda * (Kota/Provinsi Keberangkatan)</label>
              <select
                className="input-flat"
                value={form.origin_location}
                onChange={(e) => set("origin_location", e.target.value)}
                data-testid="trip-origin-input"
              >
                <option value="">Pilih kota asal</option>
                {originOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps mb-2 block">Tanggal Berangkat *</label>
              <input type="date" className="input-flat" min={todayWIB()} value={form.departure_date} onChange={(e) => set("departure_date", e.target.value)} data-testid="trip-departure-input" />
            </div>
            <div>
              <label className="label-caps mb-2 block">Tanggal Pulang *</label>
              <input type="date" className="input-flat" min={form.departure_date || todayWIB()} value={form.return_date} onChange={(e) => set("return_date", e.target.value)} data-testid="trip-return-input" />
            </div>
          </div>
          <div>
            <label className="label-caps mb-2 block">Catatan Trip</label>
            <textarea className="input-flat min-h-[100px]" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="cth. Bisa belanja di area Shibuya / Ginza" data-testid="trip-notes-input" />
          </div>
          <div className="flex justify-end pt-4">
            <button
              onClick={() => {
                if (!form.destination_country || !form.departure_date || !form.return_date) {
                  toast.error("Mohon lengkapi detail trip");
                  return;
                }
                // Iter 27 — validate dates: not in the past (WIB), return >= depart
                const today = todayWIB();
                if (form.departure_date < today) {
                  toast.error("Tanggal berangkat tidak boleh sebelum hari ini (WIB)");
                  return;
                }
                if (form.return_date < form.departure_date) {
                  toast.error("Tanggal pulang harus setelah atau sama dengan tanggal berangkat");
                  return;
                }
                if (!form.origin_location) {
                  toast.error("Mohon pilih lokasi asal keberangkatan");
                  return;
                }
                setStep(2);
              }}
              className="btn-primary-flat"
              data-testid="step-1-next"
            >
              Lanjut: Item <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6" data-testid="step-2">
          {(() => {
            // Iter 20 — Country filter: by default only show items that match this
            // trip's destination_country (case-insensitive). Toggle to "Semua" if the
            // jastiper wants to pick something across countries.
            const country = (form.destination_country || "").trim();
            const filtered = !filterByCountry || !country
              ? catalog
              : catalog.filter((it) => (it.country || "").trim().toLowerCase() === country.toLowerCase());
            const filteredIds = filtered.map((c) => c.id);
            const allFilteredSelected =
              filteredIds.length > 0 && filteredIds.every((id) => form.catalog_item_ids.includes(id));
            const selectAllFiltered = () => {
              const merged = Array.from(new Set([...form.catalog_item_ids, ...filteredIds]));
              set("catalog_item_ids", merged);
            };
            const clearAllFiltered = () => {
              set("catalog_item_ids", form.catalog_item_ids.filter((id) => !filteredIds.includes(id)));
            };
            const otherCountryCount = filterByCountry ? catalog.length - filtered.length : 0;
            return (
              <>
                {suggested.length > 0 && (
                  <div className="border-2 p-5" style={{borderColor: "var(--app-brand-soft)", backgroundColor: "var(--app-brand-soft)"}} data-testid="smart-catalog-banner">
                    <div className="flex items-center gap-2 mb-2" style={{color: "var(--app-brand)"}}>
                      <Sparkles size={16} strokeWidth={2} />
                      <span className="font-medium text-sm">Smart Catalog</span>
                    </div>
                    <p className="text-sm" style={{color: "var(--app-brand)"}}>
                      Kami otomatis pilih {suggested.length} item dari katalog Anda yang pernah dibawa dari {form.destination_country}. Ubah pilihan di bawah jika perlu.
                    </p>
                  </div>
                )}

                <div className="surface p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                    <div>
                      <div className="font-display text-xl tracking-tight">Pilih dari Katalog Saya</div>
                      <p className="text-sm" style={{color: "var(--app-muted)"}}>
                        {filterByCountry && country
                          ? <>Menampilkan item katalog dari <strong>{country}</strong>.</>
                          : <>Menampilkan SEMUA item katalog Anda.</>}
                      </p>
                    </div>
                    {/* Country filter toggle */}
                    {country && (
                      <div className="flex gap-0 border" style={{borderColor: "var(--app-border)"}} data-testid="country-filter-toggle">
                        <button
                          type="button"
                          onClick={() => setFilterByCountry(true)}
                          className="px-3 py-2 text-xs font-medium"
                          style={{
                            backgroundColor: filterByCountry ? "var(--app-brand)" : "transparent",
                            color: filterByCountry ? "white" : "var(--app-fg)",
                          }}
                          data-testid="filter-this-country-btn"
                        >
                          Hanya {country}
                        </button>
                        <button
                          type="button"
                          onClick={() => setFilterByCountry(false)}
                          className="px-3 py-2 text-xs font-medium border-l"
                          style={{
                            borderColor: "var(--app-border)",
                            backgroundColor: !filterByCountry ? "var(--app-brand)" : "transparent",
                            color: !filterByCountry ? "white" : "var(--app-fg)",
                          }}
                          data-testid="filter-all-btn"
                        >
                          Semua ({catalog.length})
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Bulk selection bar */}
                  {filteredIds.length > 0 && (
                    <div
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border p-2 mb-4 text-sm"
                      style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface-alt)"}}
                      data-testid="bulk-select-bar"
                    >
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={() => (allFilteredSelected ? clearAllFiltered() : selectAllFiltered())}
                          className="w-4 h-4 cursor-pointer"
                          data-testid="select-all-filtered-checkbox"
                        />
                        <span className="font-medium">
                          {allFilteredSelected ? "Batal pilih semua" : "Pilih semua"}
                          <span style={{color: "var(--app-muted)"}}> ({filteredIds.length} item)</span>
                        </span>
                      </label>
                      <div className="text-xs font-mono" style={{color: "var(--app-muted)"}}>
                        {filteredIds.filter((id) => form.catalog_item_ids.includes(id)).length} terpilih
                        {otherCountryCount > 0 && <span className="ml-2">· {otherCountryCount} item lain disembunyikan</span>}
                      </div>
                    </div>
                  )}

                  {catalog.length === 0 ? (
                    <div className="border border-dashed p-8 text-center text-sm" style={{borderColor: "var(--app-border)", color: "var(--app-muted)"}}>
                      Belum ada item tersimpan. Tambahkan item pertama di bawah.
                    </div>
                  ) : filtered.length === 0 ? (
                    <div className="border border-dashed p-8 text-center text-sm space-y-2" style={{borderColor: "var(--app-border)", color: "var(--app-muted)"}} data-testid="empty-filtered-catalog">
                      <div>Tidak ada item katalog untuk <strong>{country}</strong>.</div>
                      <button
                        type="button"
                        onClick={() => setFilterByCountry(false)}
                        className="text-xs underline"
                        style={{color: "var(--app-brand)"}}
                      >
                        Tampilkan semua katalog
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="catalog-filtered-grid">
                      {filtered.map((it) => {
                        const selected = form.catalog_item_ids.includes(it.id);
                        const isSuggested = suggested.find((s) => s.id === it.id);
                        const photos = (Array.isArray(it.photos) && it.photos.length) ? it.photos : (it.photo_path ? [it.photo_path] : []);
                        const main = photos[0];
                        return (
                          <button
                            key={it.id}
                            type="button"
                            onClick={() => toggleCatalog(it.id)}
                            className="relative border text-left transition-all"
                            style={{
                              borderColor: selected ? "var(--app-brand)" : "var(--app-border)",
                              boxShadow: selected ? "0 0 0 2px var(--app-brand)" : "none",
                            }}
                            data-testid={`catalog-item-${it.id}`}
                          >
                            <div className="aspect-square overflow-hidden relative" style={{backgroundColor: "var(--app-surface-alt)"}}>
                              {main ? <img src={fileUrl(main)} alt={it.name} className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
                              {photos.length > 1 && (
                                <div className="absolute bottom-1 left-1 px-1 py-0.5 text-[9px] font-mono rounded text-white" style={{backgroundColor: "rgba(0,0,0,0.7)"}} data-testid={`item-photo-count-${it.id}`}>
                                  +{photos.length - 1}
                                </div>
                              )}
                            </div>
                            <div className="p-2">
                              <div className="text-xs font-medium truncate">{it.name}</div>
                              <div className="text-xs font-mono" style={{color: "var(--app-muted)"}}>Rp {parseFloat(it.price).toLocaleString("id-ID")}</div>
                              {it.country && (
                                <div className="text-[10px] font-mono mt-0.5" style={{color: "var(--app-muted)"}}>{it.country}</div>
                              )}
                            </div>
                            {selected && (
                              <div className="absolute top-2 right-2 w-6 h-6 text-white flex items-center justify-center" style={{backgroundColor: "var(--app-brand)"}}>
                                <Check size={14} strokeWidth={2} />
                              </div>
                            )}
                            {isSuggested && (
                              <div className="absolute top-2 left-2 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide rounded text-white" style={{backgroundColor: "var(--app-brand)"}}>
                                ✨ smart
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          <div className="surface p-6 sm:p-8">
            <div className="font-display text-lg sm:text-xl mb-4 tracking-tight">Tambah Item Baru ke Katalog</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input className="input-flat" placeholder="Nama item" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} data-testid="new-item-name" />
              <input type="number" className="input-flat" placeholder="Harga Modal (IDR)" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} data-testid="new-item-price" />
              <input className="input-flat sm:col-span-2" placeholder="Deskripsi singkat" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} data-testid="new-item-desc" />
              <div className="sm:col-span-2">
                <ImageUploader value={newItem.photo_path} onChange={(p) => setNewItem({ ...newItem, photo_path: p })} testid="new-item-photo" />
              </div>

              {/* Iter 27 — Jastiper fee inline */}
              <div className="sm:col-span-2 border-t pt-4 space-y-3" style={{borderColor: "var(--app-border)"}}>
                <div>
                  <div className="label-caps" style={{color: "var(--app-muted)"}}>Jastiper Fee — Cuan Anda per Item</div>
                  <p className="text-xs mt-1" style={{color: "var(--app-muted)"}}>
                    Ditambahkan ke Harga Modal. Buyer akan melihat Harga Modal + Fee sebagai harga jual final.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-0 border max-w-sm" style={{borderColor: "var(--app-border)"}}>
                  <button
                    type="button"
                    onClick={() => setNewItem({ ...newItem, fee_type: "fixed" })}
                    className="p-2.5 text-xs transition-colors"
                    style={{
                      borderRight: "1px solid var(--app-border)",
                      backgroundColor: newItem.fee_type === "fixed" ? "var(--app-brand)" : "transparent",
                      color: newItem.fee_type === "fixed" ? "#fff" : "var(--app-fg)",
                    }}
                    data-testid="new-item-fee-fixed"
                  >
                    Nominal (IDR)
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewItem({ ...newItem, fee_type: "percent" })}
                    className="p-2.5 text-xs transition-colors"
                    style={{
                      backgroundColor: newItem.fee_type === "percent" ? "var(--app-brand)" : "transparent",
                      color: newItem.fee_type === "percent" ? "#fff" : "var(--app-fg)",
                    }}
                    data-testid="new-item-fee-percent"
                  >
                    Persentase (%)
                  </button>
                </div>
                <div className="relative max-w-xs">
                  <input
                    type="number"
                    step={newItem.fee_type === "percent" ? "0.1" : "1000"}
                    min="0"
                    className="input-flat pr-14 font-mono"
                    placeholder={newItem.fee_type === "percent" ? "contoh: 10" : "contoh: 50000"}
                    value={newItem.fee_value}
                    onChange={(e) => setNewItem({ ...newItem, fee_value: e.target.value })}
                    data-testid="new-item-fee-value"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-xs" style={{color: "var(--app-muted)"}}>
                    {newItem.fee_type === "percent" ? "%" : "IDR"}
                  </span>
                </div>
                {newItem.price && parseFloat(newItem.fee_value || 0) > 0 && (() => {
                  const base = parseFloat(newItem.price) || 0;
                  const fee = newItem.fee_type === "percent" ? base * (parseFloat(newItem.fee_value) || 0) / 100 : parseFloat(newItem.fee_value) || 0;
                  return (
                    <div className="p-3 font-mono text-xs space-y-1 max-w-sm" style={{backgroundColor: "var(--app-surface-alt)", border: "1px solid var(--app-border)"}} data-testid="new-item-fee-preview">
                      <div className="flex justify-between" style={{color: "var(--app-muted)"}}><span>Harga Modal</span><span>Rp {base.toLocaleString("id-ID")}</span></div>
                      <div className="flex justify-between" style={{color: "var(--app-muted)"}}><span>+ Fee Anda</span><span>+ Rp {fee.toLocaleString("id-ID")}</span></div>
                      <div className="flex justify-between font-semibold border-t pt-1 mt-1" style={{borderColor: "var(--app-border)", color: "var(--app-brand)"}}><span>Harga jual ke buyer</span><span>Rp {(base + fee).toLocaleString("id-ID")}</span></div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <button onClick={addCatalogItem} className="btn-secondary-flat mt-4" data-testid="add-catalog-item-btn">Tambah ke Katalog</button>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="btn-secondary-flat" data-testid="step-2-back">
              <ArrowLeft size={16} strokeWidth={1.5} /> Kembali
            </button>
            <button onClick={() => setStep(3)} className="btn-primary-flat" data-testid="step-2-next">
              Review <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6" data-testid="step-3">
          <div className="surface p-8 space-y-3">
            <div className="font-display text-xl tracking-tight mb-3">Review Trip</div>
            <Row label="Tujuan" value={`${form.destination_country}${form.destination_city ? ", " + form.destination_city : ""}`} />
            <Row label="Asal" value={form.origin_location} />
            <Row label="Berangkat" value={form.departure_date} />
            <Row label="Pulang" value={form.return_date} />
            <Row label="Item Dipilih" value={`${form.catalog_item_ids.length}`} />
            {form.notes && <Row label="Catatan" value={form.notes} />}
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="btn-secondary-flat" data-testid="step-3-back">
              <ArrowLeft size={16} strokeWidth={1.5} /> Kembali
            </button>
            <button onClick={submit} disabled={loading} className="btn-primary-flat" data-testid="submit-trip-btn">
              {loading ? "Memposting..." : "Posting Trip"}
              <Plane size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-2 border-b last:border-b-0" style={{borderColor: "var(--app-border)"}}>
      <span className="label-caps">{label}</span>
      <span className="font-mono text-sm">{value}</span>
    </div>
  );
}
